import { AsyncUnzipInflate, Unzip, unzipSync } from 'fflate';

/**
 * ROM archive extraction, shared by the main app (NESPlay._extractRomFromZip) and
 * the RetroReel live-source shim (apps/clip-maker/src/live-source-shim.js). It
 * lived only as a NESPlay method; extracting it here lets both callers use ONE
 * implementation instead of copying the body (which had drifted risk).
 */

/** Default ceiling on archive member COUNT for the fail-closed (capped) path.
 *  A ROM archive holds a ROM and maybe a readme; thousands of central-directory
 *  entries is a decompression-bomb shape, rejected before any member is inflated. */
export const DEFAULT_MAX_ARCHIVE_ENTRIES = 4096;

/**
 * An archive member's uncompressed size exceeded the caller's cap. Distinct from
 * a malformed/empty archive (which returns null) so a fail-closed caller can
 * surface it as a typed rejection and fall back, rather than treating it as
 * "no ROM found".
 */
export class RomArchiveEntryTooLargeError extends Error {
  constructor(name, size, cap) {
    super(`rom archive entry ${name} is ${size} bytes (cap ${cap})`);
    this.name = 'RomArchiveEntryTooLargeError';
    this.entryName = name;
    this.size = size;
    this.cap = cap;
  }
}

/** The archive declares too many members, or its members' declared uncompressed
 *  sizes sum past the aggregate cap — a fail-closed pre-inflate rejection for an
 *  untrusted archive. Distinct from a per-member breach and from "no ROM found". */
export class RomArchiveTooManyEntriesError extends Error {
  constructor(message, { count = null, aggregate = null } = {}) {
    super(message);
    this.name = 'RomArchiveTooManyEntriesError';
    this.count = count;
    this.aggregate = aggregate;
  }
}

/** The archive could not be parsed/decompressed (corrupt central directory,
 *  truncated data, inflate failure). Distinct from a well-formed archive that
 *  simply contains no usable ROM member (which still returns null), so the
 *  fail-closed caller can preserve the underlying decode error as a cause. */
export class RomArchiveMalformedError extends Error {
  constructor(message, { cause } = {}) {
    super(message, cause === undefined ? undefined : { cause });
    this.name = 'RomArchiveMalformedError';
    this.cause = cause;
  }
}

/** Pick the ROM member from a set of {name,size} descriptors: prefer a member
 *  whose extension matches one of the system's native (non-.zip) extensions;
 *  otherwise the largest — client parity with the server's inner-member
 *  selection (fetchRomBytesByFilename). Returns the chosen descriptor, or null. */
function selectRomEntry(entries, system) {
  const nativeExts = system.extensions.filter(e => e !== '.zip');
  let native = null;
  let largest = null;
  for (const entry of entries) {
    const ext = entry.name.substring(entry.name.lastIndexOf('.')).toLowerCase();
    if (native === null && nativeExts.includes(ext)) native = entry;
    if (!largest || entry.size > largest.size) largest = entry;
  }
  return native || largest;
}

/**
 * Extract the first ROM file from a zip archive.
 * Returns { name, data: Uint8Array } or null (well-formed archive, no ROM member).
 *
 * Prefers a member whose extension matches one of the system's native (non-.zip)
 * extensions; otherwise the largest member — client parity with the server's
 * fetchRomBytesByFilename inner-member selection.
 *
 * @param {Uint8Array} zipData
 * @param {{ extensions: string[] }} system  a SYSTEMS[...] entry
 * @param {object} [opts]
 * @param {number|null} [opts.maxEntryBytes=null]  when set, enables the
 *   FAIL-CLOSED path for untrusted archives: an archive member whose UNCOMPRESSED
 *   size exceeds this is rejected BEFORE it is decompressed (RomArchiveEntryTooLargeError),
 *   the member count and aggregate uncompressed size are capped
 *   (RomArchiveTooManyEntriesError), and ONLY the single selected ROM member is
 *   inflated (never every member). Malformed/corrupt archives throw
 *   RomArchiveMalformedError. Omit (the main-app path) for byte-for-byte the
 *   original behaviour: inflate all members, return null on any failure.
 * @param {number} [opts.maxEntries=DEFAULT_MAX_ARCHIVE_ENTRIES]  member-count cap
 *   for the fail-closed path.
 * @param {number|null} [opts.maxAggregateBytes=null]  aggregate uncompressed-size
 *   cap for the fail-closed path; defaults to maxEntryBytes when null.
 */
export function inspectBoundedArchive(zipData, system, { maxEntryBytes, maxEntries, maxAggregateBytes }) {
  // Fail-closed path (RetroReel live source): enforce caps on the central
  // directory BEFORE inflating anything, then inflate only the selected member.
  const aggregateCap = Number.isFinite(maxAggregateBytes) && maxAggregateBytes > 0 ? maxAggregateBytes : maxEntryBytes;
  const entries = [];
  let aggregate = 0;
  try {
    // filter runs per central-directory entry on its declared sizes and returns
    // false, so NOTHING is inflated in this pass — it only enumerates + bounds.
    unzipSync(zipData, {
      filter: (file) => {
        if (file.originalSize > maxEntryBytes) {
          throw new RomArchiveEntryTooLargeError(file.name, file.originalSize, maxEntryBytes);
        }
        if (entries.length >= maxEntries) {
          throw new RomArchiveTooManyEntriesError(`rom archive has more than ${maxEntries} entries`, { count: entries.length + 1 });
        }
        aggregate += file.originalSize;
        if (aggregate > aggregateCap) {
          throw new RomArchiveTooManyEntriesError(`rom archive aggregate ${aggregate} > ${aggregateCap}`, { aggregate });
        }
        entries.push({ name: file.name, size: file.originalSize });
        return false;
      },
    });
  } catch (err) {
    if (err instanceof RomArchiveEntryTooLargeError || err instanceof RomArchiveTooManyEntriesError) throw err;
    throw new RomArchiveMalformedError('rom archive could not be read', { cause: err });
  }

  return selectRomEntry(entries, system);
}

async function inspectBoundedArchiveAsync(zipData, system, limits, signal, deadlineAt) {
  if (signal?.aborted || deadlineAt != null && performance.now() >= deadlineAt) {
    throw new RomArchiveMalformedError('rom archive inspection canceled');
  }
  if (typeof Worker !== 'function') {
    // The synchronous path is retained for Node test runners. A browser without
    // worker support cannot guarantee the continuation deadline.
    if (typeof process === 'undefined' || !process.versions?.node) {
      throw new RomArchiveMalformedError('rom archive inspection worker unavailable');
    }
    const target = inspectBoundedArchive(zipData, system, limits);
    return { zipData, target };
  }
  const { ZIP_INSPECT_WORKER } = await import('./rom-zip-inspect-source.js');
  if (signal?.aborted || deadlineAt != null && performance.now() >= deadlineAt) {
    throw new RomArchiveMalformedError('rom archive inspection canceled');
  }
  return new Promise((resolve, reject) => {
    let worker;
    let url;
    let timer;
    let done = false;
    const finish = (error, value) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      signal?.removeEventListener('abort', cancel);
      try { worker?.terminate(); } catch { /* preserve the inspection outcome */ }
      if (url) URL.revokeObjectURL(url);
      if (error) reject(error);
      else resolve(value);
    };
    const cancel = () => finish(new RomArchiveMalformedError('rom archive inspection canceled'));
    try {
      url = URL.createObjectURL(new Blob([ZIP_INSPECT_WORKER], { type: 'text/javascript' }));
      worker = new Worker(url);
    }
    catch (error) { finish(new RomArchiveMalformedError('rom archive inspection worker unavailable', { cause: error })); return; }
    worker.onmessage = ({ data }) => {
      if (!data || data.error) {
        const details = data?.error;
        if (details?.name === 'RomArchiveEntryTooLargeError') {
          finish(new RomArchiveEntryTooLargeError(details.entryName, details.size, details.cap));
        } else if (details?.name === 'RomArchiveTooManyEntriesError') {
          finish(new RomArchiveTooManyEntriesError(details.message,
            { count: details.count, aggregate: details.aggregate }));
        } else finish(new RomArchiveMalformedError(details?.message || 'rom archive could not be read'));
      } else finish(null, data);
    };
    worker.onerror = () => finish(new RomArchiveMalformedError('rom archive inspection worker failed'));
    signal?.addEventListener('abort', cancel, { once: true });
    timer = setTimeout(cancel, Math.max(0, (deadlineAt ?? performance.now() + 20_000) - performance.now()));
    if (signal?.aborted) return cancel();
    try { worker.postMessage({ zipData, extensions: system.extensions, ...limits }, [zipData.buffer]); }
    catch (error) { finish(new RomArchiveMalformedError('rom archive inspection transfer failed', { cause: error })); }
  });
}

export function extractRomFromZip(zipData, system, { maxEntryBytes = null, maxEntries = DEFAULT_MAX_ARCHIVE_ENTRIES, maxAggregateBytes = null } = {}) {
  // Main-app path: unchanged behaviour (inflate all, return null on any error).
  if (maxEntryBytes == null) {
    try {
      const files = unzipSync(zipData);
      const nativeExts = system.extensions.filter(e => e !== '.zip');
      for (const [name, data] of Object.entries(files)) {
        const ext = name.substring(name.lastIndexOf('.')).toLowerCase();
        if (nativeExts.includes(ext)) return { name, data };
      }
      let largest = null;
      for (const [name, data] of Object.entries(files)) {
        if (!largest || data.length > largest.data.length) largest = { name, data };
      }
      return largest;
    } catch {
      return null;
    }
  }
  const target = inspectBoundedArchive(zipData, system,
    { maxEntryBytes, maxEntries, maxAggregateBytes });
  if (!target) return null;   // well-formed, but no member to pick
  const targetName = target.name;

  let picked;
  try {
    picked = unzipSync(zipData, { filter: (file) => file.name === targetName });
  } catch (err) {
    throw new RomArchiveMalformedError('rom archive member could not be decompressed', { cause: err });
  }
  const data = picked[targetName];
  if (!data) throw new RomArchiveMalformedError('rom archive member vanished between passes');
  if (data.byteLength > maxEntryBytes) {
    throw new RomArchiveEntryTooLargeError(targetName, data.byteLength, maxEntryBytes);
  }
  if (data.byteLength !== target.size) throw new RomArchiveMalformedError('rom archive member size disagrees with directory');
  return { name: targetName, data };
}

/** Inflate in bounded chunks and stop the worker as soon as actual output breaches the cap. */
export async function extractRomFromZipBoundedAsync(zipData, system, {
  maxEntryBytes, maxEntries = DEFAULT_MAX_ARCHIVE_ENTRIES, maxAggregateBytes = null,
  signal, deadlineAt,
} = {}) {
  if (!Number.isFinite(maxEntryBytes) || maxEntryBytes <= 0) throw new RomArchiveEntryTooLargeError('', 0, maxEntryBytes);
  const inspected = await inspectBoundedArchiveAsync(zipData, system,
    { maxEntryBytes, maxEntries, maxAggregateBytes }, signal, deadlineAt);
  zipData = inspected.zipData;
  const target = inspected.target;
  if (!target) return null;
  const targetName = target.name;
  if (signal?.aborted || deadlineAt != null && performance.now() >= deadlineAt) {
    throw new RomArchiveMalformedError('rom archive extraction canceled');
  }
  return new Promise((resolve, reject) => {
    let done = false;
    let selectedFile;
    let selected = false;
    let size = 0;
    const output = new Uint8Array(maxEntryBytes);
    const remaining = deadlineAt == null ? null : Math.max(0, deadlineAt - performance.now());
    let timer;
    const finish = (error) => {
      if (done) return;
      if (!error && size !== target.size) error = new Error('rom archive member size disagrees with directory');
      done = true;
      if (timer != null) clearTimeout(timer);
      signal?.removeEventListener('abort', cancel);
      if (error) {
        try { selectedFile?.terminate(); } catch { /* retain the original extraction failure */ }
        reject(error instanceof RomArchiveEntryTooLargeError ? error
          : new RomArchiveMalformedError('rom archive member could not be decompressed', { cause: error }));
      } else resolve({ name: targetName, data: output.subarray(0, size) });
    };
    const cancel = () => finish(new Error('rom archive extraction canceled'));
    signal?.addEventListener('abort', cancel, { once: true });
    if (remaining != null) timer = setTimeout(cancel, remaining);
    const stream = new Unzip(file => {
      if (file.name !== targetName || selected) return;
      selected = true;
      selectedFile = file;
      file.ondata = (error, chunk, final) => {
        if (done) return;
        if (error) return finish(error);
        if (chunk.byteLength > maxEntryBytes - size) {
          return finish(new RomArchiveEntryTooLargeError(targetName, size + chunk.byteLength, maxEntryBytes));
        }
        output.set(chunk, size);
        size += chunk.byteLength;
        if (final) finish();
      };
      try { file.start(); } catch (error) { finish(error); }
    });
    stream.register(AsyncUnzipInflate);
    void (async () => {
      try {
        const step = 16 * 1024;
        for (let offset = 0; offset < zipData.length && !done; offset += step) {
          if (signal?.aborted || deadlineAt != null && performance.now() >= deadlineAt) return cancel();
          stream.push(zipData.subarray(offset, offset + step), offset + step >= zipData.length);
          if (offset && offset % (step * 16) === 0) await new Promise(r => setTimeout(r, 0));
        }
        if (!selected && !done) finish(new Error('rom archive member vanished between passes'));
      } catch (error) { finish(error); }
    })();
  });
}
